import { ArrowLeftSolid, ArrowRightSolid, CalendarCheckRegular } from '@allocab/allocab-icons';
import dayjs from 'dayjs';
import { useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import { TripCalendarItem } from './TripCalendarItem';
import { useSearchTrips } from '../../hooks/useSearchTrips';
import { t } from '../../services/translation/translation';
import { formatDateForParisTZ } from '../../utils/date';
import { BaseButton } from '../button/BaseButton';
import { PrimaryButton } from '../button/PrimaryButton';
import { Spinner } from '../Spinner';

export const Calendar = () => {
  const [selectedDay, setSelectedDay] = useState(dayjs().tz('Europe/Paris').valueOf());
  const selectedDayWithoutTz = dayjs(selectedDay).format('YYYY-MM-DD');
  const currentWeek = dayjs(selectedDayWithoutTz).week();
  const selectedWeek = dayjs(selectedDayWithoutTz).week(currentWeek).tz('Europe/Paris').valueOf();
  const selectedWeekWithoutTzStart = dayjs(selectedWeek).weekday(0).format('YYYY-MM-DD');
  const selectedWeekWithoutTzEnd = dayjs(selectedWeek).weekday(6).format('YYYY-MM-DD');
  const {
    data: trips,
    isLoading,
    isPreviousData,
    isFetching,
  } = useSearchTrips({
    direction: 'asc',
    orderBy: 'resume.departureAt',
    limit: 1000,
    interval: {
      start: dayjs.tz(`${selectedWeekWithoutTzStart} 00:00:00`, 'Europe/Paris').valueOf(),
      end: dayjs.tz(`${selectedWeekWithoutTzEnd} 23:59:59`, 'Europe/Paris').valueOf(),
    },
  });

  return (
    <div>
      <div className="flex flex-row bg-gray-50 dark:bg-gray-700 justify-between items-center rounded-t-lg xl:p-5  p-3 border-t border-x dark:border-gray-600">
        <p className="capitalize flex font-bold lg:text-base">
          {formatDateForParisTZ(selectedDay, t('date.month-year'), false)}
        </p>
        <div className="flex justify-end">
          <BaseButton className="max-w-[65%] lg:max-w-[100%] mr-2 lg:mr-0">
            <div className="flex lg:items-center justify-center lg:space-x-1 text-black bg-white border  font-medium rounded-lg text-sm  dark:bg-white">
              <BaseButton
                className="py-2.5"
                onClick={() => setSelectedDay(value => dayjs(value).subtract(1, 'week').valueOf())}
              >
                <ArrowLeftSolid width={16} height={16} className="fill-current flex" />
              </BaseButton>
              <div className="hidden lg:block">{t('common.week')}</div>
              <div className="hidden lg:flex text-center items-center justify-center">{currentWeek}</div>
              <BaseButton
                className="py-2.5"
                onClick={() => setSelectedDay(value => dayjs(value).add(1, 'week').valueOf())}
              >
                <ArrowRightSolid width={16} height={16} className="fill-current flex" />
              </BaseButton>
            </div>
          </BaseButton>
          <PrimaryButton onClick={() => setSelectedDay(dayjs().tz('Europe/Paris').valueOf())}>
            <div className="flex flex-row items-center gap-2">
              <CalendarCheckRegular width={16} height={16} className="fill-white mr-0 xl:mr-2" />
              <div className=" hidden lg:block">{t('common.today')}</div>
            </div>
          </PrimaryButton>
        </div>
      </div>

      <div
        className={twMerge(
          'text-gray-700  bg-white dark:bg-gray-800 dark:text-gray-400 overflow-y-scroll h-[70vh] sm:h-[52vh] xl:h-[80vh]',
        )}
      >
        <div className="text-xs xl:text-base w-[100%] m-0 p-0 ">
          <div className="top-0 sticky  shadow capitalize justify-center grid grid-cols-7 xl:grid-cols-[75px,repeat(7,minmax(0,1fr))] divide-x divide-y border-x dark:border-gray-600">
            <div className="top-0 sticky hidden dark:border-gray-600  border-t text-black capitalize dark:bg-slate-500 bg-slate-50  content-center xl:flex items-center justify-center"></div>
            {Array.from({ length: 7 }).map((_, index) => {
              const day = dayjs(selectedDay).weekday(index).valueOf();
              const isActive = dayjs(day).isSame(selectedDay, 'day');
              const isPast = dayjs(day).isBefore(Date.now(), 'day');

              return (
                <div
                  className={twJoin(
                    ' dark:border-gray-600 capitalize justify-center  flex items-center  content-center',
                    isPast
                      ? '  dark:text-white  dark:bg-slate-500 bg-slate-50  '
                      : '  dark:bg-gray-800 bg-white align-middle ',
                  )}
                  key={day}
                >
                  <div className="hidden xl:block">
                    <div key={day} className="flex flex-row min-h-[50px] items-center gap-2">
                      <div className={'text-base'}>{formatDateForParisTZ(day, 'ddd', false)}</div>
                      <div className={'text-xs lg:text-base font-bold'}>{formatDateForParisTZ(day, 'D', false)}</div>
                    </div>
                  </div>
                  <div className="xl:hidden flex ">
                    <div
                      onClick={() => setSelectedDay(day)}
                      className="min-h-[50px] flex items-center flex-col justify-center"
                      key={day}
                    >
                      <div className={isActive ? 'font-bold dark:text-white text-xs' : 'text-xs'}>
                        {formatDateForParisTZ(day, 'ddd', false)}
                      </div>
                      <div
                        className={twJoin(
                          ' texte-xs',
                          isActive
                            ? 'rounded-full h-5 w-5 items-center justify-center flex bg-[#ff8400] text-white '
                            : '',
                        )}
                      >
                        {formatDateForParisTZ(day, 'D', false)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {isFetching && isPreviousData ? <Spinner className="mt-[40px] ml-[43%] border-none absolute" /> : null}
          {isLoading ? (
            <Spinner className="w-[100%] mt-10 border-none" />
          ) : (
            <div className="dark:border-gray-600 border-x">
              {Array.from({ length: 24 }).map((_, index) => {
                const isActive = index === parseInt(formatDateForParisTZ(Date.now(), 'H'));
                const slotWithoutTz = `${selectedDayWithoutTz} ${`00${index}`.slice(-2)}:00:00`;
                const filteredTrips = trips?.items.filter(t =>
                  dayjs(t.resume.departureAt).isSame(dayjs.tz(slotWithoutTz, 'Europe/Paris'), 'hour'),
                );

                return (
                  <div
                    className="flex flex-row xl:grid xl:grid-cols-[75px,repeat(7,minmax(0,1fr))] divide-y xl:divide-x "
                    key={`${selectedDay}-${index}`}
                  >
                    {index >= 0 ? (
                      <div className="flex flex-row items-center">
                        <div
                          className={twJoin(
                            'items-center min-h-[100px] min-w-[50px] justify-center flex',
                            isActive ? ' text-[#ff8400] font-extrabold place-content-start' : '',
                          )}
                        >
                          <div>{index}h</div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-row  w-[64px]"></div>
                    )}
                    {Array.from({ length: 7 }).map((_, dayIndex) => {
                      const day = dayjs(selectedWeek).weekday(dayIndex).valueOf();
                      const dayWithoutTz = dayjs(day).format('YYYY-MM-DD');
                      const daySlotWithoutTz = `${dayWithoutTz} ${`00${index}`.slice(-2)}:00:00`;
                      const dayFilteredTrips = trips?.items.filter(t =>
                        dayjs(t.resume.departureAt).isSame(dayjs.tz(daySlotWithoutTz, 'Europe/Paris'), 'hour'),
                      );

                      return (
                        <>
                          <div
                            className={twJoin(
                              'hidden  dark:border-gray-600 flex-1 xl:flex xl:flex-col justify-center items-center p-3 gap-2',
                              isActive ? ' bg-[#ff840022] dark:bg-[#ff840044]' : '',
                            )}
                          >
                            {dayFilteredTrips?.map(trip => <TripCalendarItem key={trip.id} trip={trip} />)}
                          </div>
                        </>
                      );
                    })}
                    <div
                      className={twJoin(
                        'xl:hidden dark:border-gray-600 flex-1 flex flex-col justify-center items-center p-3 gap-2',
                        isActive ? ' bg-[#ff840022] dark:bg-[#ff840044]' : '',
                      )}
                    >
                      {filteredTrips?.map(trip => <TripCalendarItem key={trip.id} trip={trip} />)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
